import graphql__00e7705948430c2a0b08 from "./__generated__/AnalysisForecastBwaViewQuery.graphql.ts";import * as React from "react";
import {startTransition, useState} from "react";
import {useOutletContext} from "react-router";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faPencil, faPlusCircle, faTrash} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import EditBwaMonthDialog from "./dialog/EditBwaMonthDialog";
import DeleteBwaMonthDialog from "./dialog/DeleteBwaMonthDialog";
import {formatAmount} from "../helper/revenueFormat";
import {monthToDate} from "../helper/dateFormat";
import {bwaFigureNames} from "../helper/bwaFigures";
import AnalysisForecastBwaViewQueryNode from "./__generated__/AnalysisForecastBwaViewQuery.graphql";
import type {AnalysisForecastBwaViewQuery as AnalysisForecastBwaViewQueryType} from "./__generated__/AnalysisForecastBwaViewQuery.graphql";

const analysisForecastBwaViewQuery = graphql__00e7705948430c2a0b08;

type AnalysisOutletContext = {
    bwaMonthsQuery: any
};

type BwaMonthRow = AnalysisForecastBwaViewQueryType['response']['bwaMonths'][number];

type OpenDialog =
    | {type: 'record'}
    | {type: 'edit', bwaMonth: BwaMonthRow}
    | {type: 'delete', bwaMonth: BwaMonthRow}
    | null;

/**
 * The month after the newest one with BWA values, or the previous calendar month if there are none yet.
 */
const nextMonthToRecord = (bwaMonths: ReadonlyArray<{month: string}>): string => {
    let year: number;
    let month: number;
    if (bwaMonths.length > 0) {
        const [newestYear, newestMonth] = bwaMonths[0].month.split('-').map(Number);
        year = newestMonth === 12 ? newestYear + 1 : newestYear;
        month = newestMonth === 12 ? 1 : newestMonth + 1;
    } else {
        const today = new Date();
        year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
        month = today.getMonth() === 0 ? 12 : today.getMonth();
    }
    return year + '-' + String(month).padStart(2, '0');
};

// The overview of the forecast starts this many months before the current month
const FORECAST_PAST_MONTHS = 3;

/**
 * The past months shown in the forecast which have no BWA values yet, oldest first, as YYYY-MM.
 */
const monthsWithoutBwa = (bwaMonths: ReadonlyArray<{month: string}>): string[] => {
    const today = new Date();
    const missing: string[] = [];
    for (let offset = FORECAST_PAST_MONTHS; offset >= 1; offset--) {
        const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
        const month = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
        if (!bwaMonths.some(bwaMonth => bwaMonth.month === month)) {
            missing.push(month);
        }
    }
    return missing;
};

/**
 * Lists the months with BWA values. A month with values counts as closed. The closing balances of
 * the BWA are compared with the balances computed from the uploaded statements, a difference
 * is highlighted.
 */
const AnalysisForecastBwaView: React.FC = () => {
    const intl = useIntl();
    const {bwaMonthsQuery} = useOutletContext<AnalysisOutletContext>();
    const [queryRef, loadBwaMonthsQuery] = useQueryLoader<AnalysisForecastBwaViewQueryType>(AnalysisForecastBwaViewQueryNode, bwaMonthsQuery);
    const data = usePreloadedQuery<AnalysisForecastBwaViewQueryType>(analysisForecastBwaViewQuery, queryRef ?? bwaMonthsQuery);
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

    const bwaMonths = data.bwaMonths;
    const missingMonths = monthsWithoutBwa(bwaMonths);

    const reloadBwaMonths = () => {
        startTransition(() => {
            loadBwaMonthsQuery({}, {fetchPolicy: 'network-only'});
        });
    };

    const closeDialog = (changed: boolean) => {
        setOpenDialog(null);
        if (changed) {
            reloadBwaMonths();
        }
    };

    return (
        <>
            <Panel
                headline={intl.formatMessage({id: 'view.analysisForecastBwa.headline'})}
                buttons={
                    <ActionButton
                        icon={faPlusCircle}
                        title={intl.formatMessage({id: 'view.analysisForecastBwa.recordButton'})}
                        onClick={() => setOpenDialog({type: 'record'})}
                    />
                }
                fullWidthChildren={true}
            >
                <p className="px-4 py-3 sm:px-6 text-sm text-gray-500 border-b border-gray-200">
                    {intl.formatMessage({id: 'view.analysisForecastBwa.description'})}
                </p>
                {missingMonths.length > 0 && (
                    <p className="px-4 py-3 sm:px-6 text-sm text-yellow-800 bg-yellow-50 border-b border-gray-200">
                        {intl.formatMessage({id: 'view.analysisForecastBwa.missing'}, {
                            months: intl.formatList(missingMonths.map(month => intl.formatDate(monthToDate(month), {month: 'long', year: 'numeric', timeZone: 'UTC'})), {type: 'conjunction'}),
                        })}
                    </p>
                )}
                {bwaMonths.length === 0 ? (
                    <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastBwa.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.analysisForecastBwa.col.month'})}</TableHeader>
                                    {bwaFigureNames.map(column => (
                                        <TableHeader key={column} className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastBwa.col.' + column})}</TableHeader>
                                    ))}
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastBwa.col.closingBalances'})}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastBwa.col.differences'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {bwaMonths.map((bwaMonth, index) => (
                                    <tr key={bwaMonth.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData className="pl-4 sm:pl-6 pr-3 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">
                                                {intl.formatDate(bwaMonth.month + '-01', {month: 'long', year: 'numeric', timeZone: 'UTC'})}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {bwaMonth.changedBy
                                                    ? intl.formatMessage({id: 'view.analysisForecastBwa.changedBy'}, {user: bwaMonth.changedBy, date: intl.formatDate(bwaMonth.changedAt, {dateStyle: 'medium'})})
                                                    : intl.formatMessage({id: 'view.analysisForecastBwa.recordedBy'}, {user: bwaMonth.recordedBy, date: intl.formatDate(bwaMonth.recordedAt, {dateStyle: 'medium'})})}
                                            </div>
                                        </TableData>
                                        {bwaFigureNames.map(column => (
                                            <TableData key={column} className="px-3 whitespace-nowrap" textAlignment={TextAlignment.Right}>{formatAmount(bwaMonth.figures[column])}</TableData>
                                        ))}
                                        <TableData className="px-3 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            {bwaMonth.balances.map(balance => (
                                                <div key={balance.bankAccountIdentifier}>
                                                    <span className="text-xs text-gray-500 mr-2">{balance.bankAccountName}</span>
                                                    {formatAmount(balance.closingBalance)}
                                                </div>
                                            ))}
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            {bwaMonth.balances.map(balance => (
                                                <div key={balance.bankAccountIdentifier} className={balance.difference === 0 ? 'text-gray-400' : 'font-medium text-red-700 bg-red-50 rounded-sm px-1'}>
                                                    {balance.difference === 0 ? '0' : (balance.difference > 0 ? '+' : '') + formatAmount(balance.difference)}
                                                </div>
                                            ))}
                                        </TableData>
                                        <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            <ActionButton
                                                icon={faPencil}
                                                title={intl.formatMessage({id: 'view.analysisForecastBwa.action.edit'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'edit', bwaMonth})}
                                            />
                                            <ActionButton
                                                icon={faTrash}
                                                title={intl.formatMessage({id: 'view.analysisForecastBwa.action.delete'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'delete', bwaMonth})}
                                            />
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>

            {openDialog?.type === 'record' && (
                <EditBwaMonthDialog bankAccounts={data.bankAccounts} defaultMonth={nextMonthToRecord(bwaMonths)} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'edit' && (
                <EditBwaMonthDialog bwaMonth={openDialog.bwaMonth} bankAccounts={data.bankAccounts} defaultMonth={openDialog.bwaMonth.month} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'delete' && (
                <DeleteBwaMonthDialog bwaMonth={openDialog.bwaMonth} onClose={closeDialog}/>
            )}
        </>
    );
};

export default AnalysisForecastBwaView;
